import * as React from 'react';

import { Button, Context, Draggable, Grid, Spacer, Text, useTheme, cn, textToHTML } from '@siteground/styleguide';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { saveChatStylesAfterDrag } from '../../actions/chat-drag';
import { chatClosedByClient, closeChatUIWindow } from '../../app-communication/actions/from-client-frontend';
import { CHAT_LAYOUT_DOM_ID } from '../../constants/dom-ids';
import { getChatPosition, getChatSettings, getIsChatMessagingInProgress } from '../../selectors/chat';
import { ChatContext } from '../chat-context';
import { ChatNotifications } from '../chat-notifications';
import { MinimizedState } from '../minimized-state';
import { OfflineBanner } from '../offline-banner';
import { PartialLoader } from '../partial-loader';
import ChatHeaderActions from './chat-header-actions';
import { resizeChatHandler, stopResize } from './chat-resize-handler';
import { getOptions } from '../../selectors/options';
import './chat-layout.scss';

const chatSizes = ['small', 'large'];

/**
 * @typedef {Object} Props
 * @property {string} [className]
 * @property {boolean} [enableActionMenu]
 * @property {*} content
 * @property {*} contentPadding - TODO use SG types from build folder
 * @property {"content"|"footer"} expand
 * @property {*} [header]
 * @property {*} headerPadding - TODO use SG types from build folder
 * @property {*} [footer]
 * @property {*} footerPadding - TODO use SG types from build folder
 */

/** @param {Props} props */
const ChatLayout = (props) => {
  const { content, expand, header, footer, enableActionMenu } = props;
  const [domRef, setDomRef] = React.useState(null);
  const [isUsingKeyboard, setIsUsingKeyboard] = React.useState(false);
  const prevPropDevice = React.useRef(null);
  const { theme, size, isEasyReadActive } = useSelector(getOptions);
  const { setTheme, setEasyRead } = useTheme();
  const { minimized, shaking } = useSelector(getChatSettings);
  const { left, top, width, height } = useSelector(getChatPosition);
  const isChatMessagingInProgress = useSelector(getIsChatMessagingInProgress);

  const [confirmChatClose, setConfirmChatClose] = React.useState(false);

  const { device } = React.useContext(Context);

  const dispatch = useDispatch();
  const intl = useIntl();

  const shouldAskForCloseConfirmation = confirmChatClose && isChatMessagingInProgress;
  const shouldShowFooter = shouldAskForCloseConfirmation || Boolean(footer);

  React.useEffect(() => {
    setTheme(theme);
    setEasyRead(isEasyReadActive);
  }, [theme, isEasyReadActive]);

  React.useEffect(() => {
    // When minimizing the chat, we want to return it to its initial position
    if (minimized) {
      dispatch(
        saveChatStylesAfterDrag({
          left: null,
          top: null,
          width: null,
          height: null
        })
      );
      stopResize();
    }
  }, [minimized]);

  React.useEffect(() => {
    if (prevPropDevice.current && prevPropDevice.current.isLandscape !== device.isLandscape) {
      dispatch(
        saveChatStylesAfterDrag({
          left: null,
          top: null,
          width: null,
          height: null
        })
      );
    }

    prevPropDevice.current = device;
    stopResize();
  }, [device]);

  React.useEffect(() => {
    const handleKeyDown = () => setIsUsingKeyboard(true);
    const handleMouseDown = () => setIsUsingKeyboard(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const sizeToUse = chatSizes.includes(size) ? size : 'small';
  const chatClasses = cn(
    'chat',
    isUsingKeyboard && 'sg-keyboard',
    shaking && 'chat--animation-shake',
    minimized ? 'chat--minimized' : `chat-${sizeToUse}--opened`
  );

  const headerClasses = cn('chat__header');

  const contentClasses = cn(
    'chat__content',
    !shouldShowFooter && 'chat__content__footer',
    expand === 'content' && 'chat__content--expand'
  );

  const footerClasses = cn('chat__footer', expand === 'footer' && 'chat__footer--expand');

  const saveStyles = () => {
    dispatch(
      saveChatStylesAfterDrag({
        left: domRef.style.left,
        top: domRef.style.top,
        width: domRef.style.width,
        height: domRef.style.height
      })
    );
  };

  const initDomRef = (newRef) => {
    setDomRef(newRef);
    if (device.isDevicePhone || device.isDeviceTablet) {
      return false;
    }

    resizeChatHandler(domRef);
  };

  const showResizer = () => {
    if (minimized) {
      return false;
    }

    if (device.isDevicePhone || device.isDeviceTablet) {
      return false;
    }

    return true;
  };

  return (
    <ChatContext.Provider value={{ shouldAutoScroll: true }}>
      <div ref={initDomRef} id={CHAT_LAYOUT_DOM_ID} className={chatClasses} style={{ left, top, width, height }}>
        {!minimized && <ChatNotifications />}

        <PartialLoader>
          <Draggable
            elementToDrag={domRef}
            className={headerClasses}
            canDrag={(event) => {
              const nonDraggable = document.querySelector('[data-none-draggable]');

              if (device.isDevicePhone || device.isDeviceTablet) {
                return false;
              }

              if (nonDraggable && nonDraggable.contains(event.target)) {
                return false;
              }

              return true;
            }}
            onPointerUp={saveStyles}
          >
            {header}

            <Spacer />

            <ChatHeaderActions
              data-none-draggable
              enableActionMenu={enableActionMenu}
              setConfirmChatClose={setConfirmChatClose}
            />
          </Draggable>

          {!minimized && <OfflineBanner />}

          <div className={contentClasses}>{content}</div>

          {shouldShowFooter && (
            <div className={footerClasses}>
              {confirmChatClose ? (
                <Grid gap="large" padding="large" justifyItems="center" style={{ width: '100%' }}>
                  <Text align="center" size="large">
                    {textToHTML(intl.formatMessage({ id: 'translate.chat.end.confirm.message' }))}
                  </Text>

                  <Grid gap="x-small" autoflow="column">
                    <Button color="primary" type="ghost" onClick={() => setConfirmChatClose(false)}>
                      {intl.formatMessage({ id: 'translate.generic.cancel' })}
                    </Button>

                    <Button
                      color="primary"
                      onClick={() =>
                        dispatch(
                          chatClosedByClient({
                            onFailure: () => dispatch(closeChatUIWindow())
                          })
                        )
                      }
                    >
                      {intl.formatMessage({ id: 'translate.generic.confirm' })}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                footer
              )}
            </div>
          )}

          {minimized && <MinimizedState />}
        </PartialLoader>

        {showResizer() && (
          <React.Fragment>
            <div className="chat__resizer chat__resizer--left" data-resizer="resize-left" />
            <div className="chat__resizer chat__resizer--right" data-resizer="resize-right" />
            <div className="chat__resizer chat__resizer--bottom" data-resizer="resize-bottom" />
            <div className="chat__resizer chat__resizer--top" data-resizer="resize-top" />

            <div className="chat__resizer chat__resizer--top-left" data-resizer="resize-top-left" />
            <div className="chat__resizer chat__resizer--top-right" data-resizer="resize-top-right" />
            <div className="chat__resizer chat__resizer--bottom-left" data-resizer="resize-bottom-left" />
            <div className="chat__resizer chat__resizer--bottom-right" data-resizer="resize-bottom-right" />
          </React.Fragment>
        )}
      </div>
    </ChatContext.Provider>
  );
};

ChatLayout.defaultProps = {
  expand: 'content'
};

export default ChatLayout;
